import { Button, Modal } from 'antd';
import { createContext, useContext, useMemo, useRef, useState } from 'react';

import { tc } from 'utils/i18nUtil';

interface Content {
  title: string;
  message: string;
  saveText?: string;
}

type ModalResult = boolean | 'save';

const ModalContext = createContext<
  | {
      isOpen: boolean;
      openModal: (open: boolean) => Promise<ModalResult>;
      content: Content;
      setContent: (content: Content) => void;
    }
  | undefined
>(undefined);

export const NavigateConfirmationModal = ({ children }: { children?: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [content, setContent] = useState<Content>({
    title: '',
    message: '',
  });
  const resolveRef = useRef<(data: ModalResult) => void>((data: ModalResult) => {});

  const value = useMemo(() => {
    return {
      isOpen,
      openModal: (open: boolean) => {
        setIsOpen(open);
        return new Promise<ModalResult>((resolve) => {
          resolveRef.current = resolve;
        });
      },
      content,
      setContent: (content: Content) => setContent(content),
    };
  }, [content, isOpen]);

  const handleConfirm = () => {
    resolveRef.current(true);
    setIsOpen(false);
  };

  const handleSave = () => {
    resolveRef.current('save');
    setIsOpen(false);
  };

  const handleCancel = () => {
    resolveRef.current(false);
    setIsOpen(false);
  };

  // When a save option is available: show "Discard Changes" + "Save Changes" buttons.
  // The X button closes the modal and keeps the user on the current page.
  const footer = content.saveText
    ? [
        <Button key="discard" onClick={handleConfirm}>
          {tc('discard_changes')}
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          {content.saveText}
        </Button>,
      ]
    : undefined;

  return (
    <ModalContext.Provider value={value}>
      {children}
      <Modal
        title={content.title}
        destroyOnClose
        visible={isOpen}
        footer={footer}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText={tc('exit')}>
        <div style={{ whiteSpace: 'pre-line' }}>{content.message}</div>
      </Modal>
    </ModalContext.Provider>
  );
};

export const useNavigateConfirmationModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error(`useNavigateConfirmationModal must be used within NavigateConfirmationModal`);
  }
  return context;
};
